/**
 * Which classification rules this file was classified with — intake only.
 *
 * Collapsed by default: the figures above already say what the rows came to,
 * this says why. The header line is the summary; the rules are one click away.
 * Auditors never see it — rules are not theirs to read.
 */

import { useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ErrorNotice, StatusBadge } from '../../shared/ui';
import { count, dateTime } from '../../utils/format';
import type { FileClassificationRuleRecord } from '../../schemas';

export function FileClassificationSection({
	classifiedAt,
	recorded,
	loading,
	error,
	onRetry,
	labelOf,
	canReapply,
	reapplyDisabled,
	reapplyTitle,
	onReapply,
}: {
	classifiedAt: string | null;
	recorded: FileClassificationRuleRecord | undefined;
	loading: boolean;
	error: unknown;
	onRetry: () => void;
	labelOf: (key: string) => string;
	/** The re-apply action is for people who may map formats; others never see it. */
	canReapply: boolean;
	reapplyDisabled: boolean;
	reapplyTitle?: string;
	onReapply: () => void;
}) {
	const [open, setOpen] = useState(false);
	const when = classifiedAt ?? recorded?.classifiedAt ?? null;

	const summary = (() => {
		if (!recorded)
			return loading ? (
				<CircularProgress size={12} aria-label="Loading" />
			) : null;
		if (recorded.classifiedAt === null && !when)
			return 'Not classified yet';
		const parts: string[] = [];
		if (recorded.hasSelection === false)
			parts.push('Every published rule applied');
		else
			parts.push(
				`${count(recorded.recorded.length)} ${
					recorded.recorded.length === 1 ? 'rule' : 'rules'
				} applied`,
			);
		if (when) parts.push(`Last applied ${dateTime(when)}`);
		return parts.join(' · ');
	})();

	return (
		<section className="card">
			<Box
				component="header"
				className="card-hd"
				sx={{ alignItems: 'center' }}
			>
				{/* The heading holds the toggle (disclosure pattern), not the reverse:
            a heading inside a button is not valid HTML. */}
				<h2 style={{ flex: '1 1 auto', minWidth: 0, margin: 0 }}>
					<ButtonBase
						onClick={() => setOpen((current) => !current)}
						aria-expanded={open}
						aria-controls="classification-rules"
						sx={{
							width: '100%',
							justifyContent: 'flex-start',
							textAlign: 'left',
							gap: 'var(--s2)',
							font: 'inherit',
						}}
					>
						<ExpandMoreIcon
							fontSize="small"
							sx={{
								transform: open ? 'none' : 'rotate(-90deg)',
								color: 'var(--ink-3)',
							}}
						/>
						<span>Classification</span>
						{summary && (
							<span
								className="small mut"
								style={{ fontWeight: 400 }}
							>
								{summary}
							</span>
						)}
					</ButtonBase>
				</h2>
				{canReapply && (
					<Button
						variant="outlined"
						size="small"
						disabled={reapplyDisabled}
						title={reapplyTitle}
						onClick={onReapply}
					>
						Re-apply rules
					</Button>
				)}
			</Box>

			{/* Always in the page so `aria-controls` names a real element. */}
			<Box id="classification-rules" className="card-bd" hidden={!open}>
				{open && (
					<>
						<p
							className="small mut"
							style={{ marginBottom: 'var(--s2)' }}
						>
							Re-applying rules changes this file only. Audits
							that already use its rows keep them as they were.
						</p>
						{Boolean(error) && (
							<ErrorNotice error={error} onRetry={onRetry} />
						)}
						{/* Rendered from each entry's own recorded match fields, not a lookup
              by rule: once a rule is no longer published those are the only
              description of what ran. */}
						{recorded &&
							(recorded.classifiedAt === null && !when ? (
								<p className="small mut">Not classified yet.</p>
							) : recorded.hasSelection === false ? (
								<p className="small">
									No selection was recorded — every published
									rule ran.
								</p>
							) : recorded.recorded.length > 0 ? (
								<div className="divide">
									{recorded.recorded.map((rule, index) => (
										<div
											/* Prefixed: rule ids are small integers, so a bare index
                       would collide with a real id. */
											key={
												rule.ruleId
													? `rule-${rule.ruleId}`
													: `row-${index}`
											}
											style={{
												paddingTop:
													index === 0
														? 0
														: 'var(--s1)',
											}}
										>
											<span className="small">
												{rule.matchField &&
												rule.matchOperator &&
												rule.classification ? (
													<>
														When{' '}
														{labelOf(
															rule.matchField,
														)}{' '}
														{rule.matchOperator
															.toLowerCase()
															.replace(
																/_/g,
																' ',
															)}{' '}
														“{rule.matchValue}” →{' '}
														<StatusBadge
															group="classification"
															value={
																rule.classification
															}
															plain
														/>
													</>
												) : (
													<span className="mut">
														Rule detail not
														recorded.
													</span>
												)}
												{!rule.stillPublished && (
													<span
														className="bdg warning plain"
														style={{
															marginLeft: 6,
														}}
													>
														No longer published
													</span>
												)}
											</span>
										</div>
									))}
								</div>
							) : (
								<p className="small mut">Not classified yet.</p>
							))}
					</>
				)}
			</Box>
		</section>
	);
}
